import { useEffect, useState } from "react";
import { api, getAdminToken } from "@/lib/api";
import { toast } from "sonner";
import { Save, Trash2, Plus, Upload, Eye, Map } from "lucide-react";

const OBJECTS = ["Pièce d'or", "Clé noire", "Miroir", "Boussole", "Plume", "Pierre rouge"];
const C_KEYS = ["corps", "coeur", "cerveau", "conscience"];

const pw = () => ({ headers: { Authorization: `Bearer ${getAdminToken()}` } });
const blankChoice = () => ({ key: "", label: "", destination: "", requiredObject: "", objectReward: "", hidden: false, C_effect: {} });
const blankScene = (bookId) => ({ _new: true, _tmp: `tmp_${Date.now()}`, scene_id: "", step: 1, title: "", character: "", text: "", journalPrompt: "", isPremium: true, isEnding: false, choices: [] });

export default function AdminAdventure() {
  const [books, setBooks] = useState([]);
  const [bookEdit, setBookEdit] = useState(null);
  const [scenesBook, setScenesBook] = useState(null);
  const [scenes, setScenes] = useState([]);
  const [importText, setImportText] = useState("");
  const [replace, setReplace] = useState(false);
  const [preview, setPreview] = useState(null);
  const [showMap, setShowMap] = useState(false);
  const [previewMobile, setPreviewMobile] = useState(false);

  useEffect(() => { api.get("/adventure/books").then((r) => setBooks(r.data)); }, []);

  const saveBook = async () => {
    const r = await api.put(`/admin/adventure/books/${bookEdit.id}`, {
      title: bookEdit.title, tagline: bookEdit.tagline, available: bookEdit.available,
      start_scene: bookEdit.start_scene, total_steps: Number(bookEdit.total_steps) || 21,
    }, pw());
    setBooks((bs) => bs.map((b) => (b.id === r.data.id ? r.data : b)));
    setBookEdit(null);
    toast.success("Aventure enregistrée");
  };

  const openScenes = async (book) => {
    const r = await api.get(`/admin/adventure/${book.id}/scenes`, pw());
    setScenes(r.data);
    setScenesBook(book);
  };

  const setScene = (idx, patch) => setScenes((s) => s.map((sc, i) => (i === idx ? { ...sc, ...patch } : sc)));
  const setChoice = (si, ci, patch) => setScenes((s) => s.map((sc, i) => i === si ? { ...sc, choices: sc.choices.map((c, j) => j === ci ? { ...c, ...patch } : c) } : sc));
  const addChoice = (si) => setScenes((s) => s.map((sc, i) => i === si ? { ...sc, choices: [...(sc.choices || []), blankChoice()] } : sc));
  const removeChoice = (si, ci) => setScenes((s) => s.map((sc, i) => i === si ? { ...sc, choices: sc.choices.filter((_, j) => j !== ci) } : sc));

  const payload = (sc) => ({
    scene_id: sc.scene_id, step: Number(sc.step) || 1, title: sc.title || "", character: sc.character || "",
    text: sc.text || "", journalPrompt: sc.journalPrompt || "", isPremium: !!sc.isPremium, isEnding: !!sc.isEnding,
    choices: (sc.choices || []).map((c) => ({
      key: c.key, label: c.label, destination: c.destination, requiredObject: c.requiredObject || "",
      objectReward: c.objectReward || "", hidden: !!c.hidden,
      C_effect: Object.fromEntries(Object.entries(c.C_effect || {}).filter(([, v]) => Number(v)).map(([k, v]) => [k, Number(v)])),
    })),
  });

  const saveScene = async (idx) => {
    const sc = scenes[idx];
    if (!sc.scene_id) { toast.error("Identifiant de scène requis"); return; }
    try {
      if (sc._new) {
        const r = await api.post(`/admin/adventure/${scenesBook.id}/scenes`, payload(sc), pw());
        setScenes((s) => s.map((x, i) => (i === idx ? r.data : x)));
      } else {
        const r = await api.put(`/admin/adventure/scenes/${sc.id}`, payload(sc), pw());
        setScenes((s) => s.map((x, i) => (i === idx ? r.data : x)));
      }
      toast.success(`Scène ${sc.scene_id} enregistrée`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec");
    }
  };

  const deleteScene = async (idx) => {
    const sc = scenes[idx];
    if (sc._new) { setScenes((s) => s.filter((_, i) => i !== idx)); return; }
    await api.delete(`/admin/adventure/scenes/${sc.id}`, pw());
    setScenes((s) => s.filter((_, i) => i !== idx));
    toast.success("Scène supprimée");
  };

  const doImport = async () => {
    let parsed;
    try {
      parsed = JSON.parse(importText);
      if (!Array.isArray(parsed)) parsed = parsed.scenes;
    } catch { toast.error("JSON invalide"); return; }
    try {
      const r = await api.post(`/admin/adventure/${scenesBook.id}/import`, { scenes: parsed, replace }, pw());
      toast.success(`${r.data.imported} scène(s) importée(s)`);
      const sr = await api.get(`/admin/adventure/${scenesBook.id}/scenes`, pw());
      setScenes(sr.data);
      setImportText("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'import");
    }
  };

  return (
    <div data-testid="admin-adventure">
      <div className="space-y-3">
        {books.map((b) => (
          <div key={b.id} className="rounded-xl bg-white border border-[#E8DCC4] p-4 flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[180px]">
              <p className="font-serif text-lg text-[#1F2A24]">{b.title}</p>
              <p className="font-ui text-[11px] tracking-widest text-[#C9A24B]">{b.available ? "DISPONIBLE" : "À VENIR"}</p>
            </div>
            <button data-testid={`adv-edit-book-${b.slug}`} onClick={() => setBookEdit({ ...b })} className="rounded-full bg-[#1F2A24] text-white px-4 py-2 font-ui text-xs hover:bg-black">MODIFIER</button>
            <button data-testid={`adv-scenes-${b.slug}`} onClick={() => openScenes(b)} className="rounded-full border border-[#385E3A] text-[#385E3A] px-4 py-2 font-ui text-xs hover:bg-[#385E3A]/10">MODIFIER LES SCÈNES</button>
          </div>
        ))}
      </div>

      {bookEdit && (
        <Modal onClose={() => setBookEdit(null)} testid="adv-book-modal" title={`Aventure — ${bookEdit.title}`}>
          <Field label="Titre"><input data-testid="adv-book-title" value={bookEdit.title || ""} onChange={(e) => setBookEdit({ ...bookEdit, title: e.target.value })} className={inp} /></Field>
          <Field label="Accroche"><textarea data-testid="adv-book-tagline" rows={2} value={bookEdit.tagline || ""} onChange={(e) => setBookEdit({ ...bookEdit, tagline: e.target.value })} className={ta} /></Field>
          <Field label="Scène de départ (scene_id)"><input value={bookEdit.start_scene || ""} onChange={(e) => setBookEdit({ ...bookEdit, start_scene: e.target.value })} className={inp} /></Field>
          <Field label="Nombre d'étapes"><input type="number" value={bookEdit.total_steps || 21} onChange={(e) => setBookEdit({ ...bookEdit, total_steps: e.target.value })} className={inp} /></Field>
          <label className="flex items-center gap-2 font-sans text-sm mt-1"><input type="checkbox" data-testid="adv-book-available" checked={!!bookEdit.available} onChange={(e) => setBookEdit({ ...bookEdit, available: e.target.checked })} /> Disponible (jouable)</label>
          <div className="flex justify-end gap-3 mt-5">
            <button onClick={() => setBookEdit(null)} className="rounded-full px-5 py-2.5 font-ui text-sm text-[#1F2A24]/70">Annuler</button>
            <button data-testid="adv-book-save" onClick={saveBook} className="inline-flex items-center gap-2 rounded-full bg-[#385E3A] text-white px-6 py-2.5 font-ui text-sm"><Save size={16} /> ENREGISTRER</button>
          </div>
        </Modal>
      )}

      {scenesBook && (
        <Modal onClose={() => setScenesBook(null)} testid="adv-scenes-modal" wide title={`Scènes — ${scenesBook.title}`}>
          <details className="rounded-xl border border-[#C9A24B]/40 bg-[#F8F5EE] p-3 mb-4">
            <summary className="font-ui text-sm text-[#1F2A24] cursor-pointer inline-flex items-center gap-2"><Upload size={14} /> Importer un manuscrit (JSON)</summary>
            <p className="font-sans text-xs text-[#1F2A24]/60 mt-2">Collez un tableau JSON de scènes (sceneId → scene_id, step, title, text, choices…).</p>
            <textarea data-testid="adv-import-text" rows={6} value={importText} onChange={(e) => setImportText(e.target.value)} placeholder='[{"scene_id":"J06","step":6,"title":"…","text":"…","isPremium":true,"choices":[{"key":"A","label":"…","destination":"J07"}]}]' className={`${ta} font-mono text-xs`} />
            <label className="flex items-center gap-2 font-sans text-xs mt-2"><input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} /> Remplacer toutes les scènes existantes</label>
            <button data-testid="adv-import-run" onClick={doImport} className="mt-2 rounded-full bg-[#1F2A24] text-white px-5 py-2 font-ui text-xs">IMPORTER</button>
          </details>

          <div className="flex flex-wrap gap-2 mb-3">
            <button data-testid="adv-add-scene" onClick={() => setScenes((s) => [blankScene(scenesBook.id), ...s])} className="inline-flex items-center gap-1.5 rounded-full border border-[#385E3A] text-[#385E3A] px-4 py-2 font-ui text-xs"><Plus size={14} /> Ajouter une scène</button>
            <button data-testid="adv-map-open" onClick={() => setShowMap(true)} className="inline-flex items-center gap-1.5 rounded-full border border-[#2E5B7F] text-[#2E5B7F] px-4 py-2 font-ui text-xs"><Map size={14} /> Carte du parcours</button>
          </div>

          <div className="space-y-2">
            {scenes.map((sc, i) => (
              <details key={sc.id || sc._tmp} data-testid={`adv-scene-${sc.scene_id || "new"}`} className="rounded-xl border border-[#E8DCC4] p-3">
                <summary className="font-serif text-[#1F2A24] cursor-pointer">{sc.scene_id || "(nouvelle)"} · étape {sc.step} — {sc.title || "sans titre"}</summary>
                <div className="mt-3 grid sm:grid-cols-2 gap-2">
                  <Field label="scene_id"><input value={sc.scene_id || ""} onChange={(e) => setScene(i, { scene_id: e.target.value })} className={inp} /></Field>
                  <Field label="Étape"><input type="number" value={sc.step} onChange={(e) => setScene(i, { step: e.target.value })} className={inp} /></Field>
                  <Field label="Titre"><input value={sc.title || ""} onChange={(e) => setScene(i, { title: e.target.value })} className={inp} /></Field>
                  <Field label="Personnage"><input value={sc.character || ""} onChange={(e) => setScene(i, { character: e.target.value })} className={inp} /></Field>
                </div>
                <Field label="Texte narratif"><textarea rows={3} value={sc.text || ""} onChange={(e) => setScene(i, { text: e.target.value })} className={ta} /></Field>
                <Field label="Carnet du héros (question)"><textarea rows={2} value={sc.journalPrompt || ""} onChange={(e) => setScene(i, { journalPrompt: e.target.value })} className={ta} /></Field>
                <div className="flex gap-4 mt-1">
                  <label className="flex items-center gap-2 font-sans text-sm"><input type="checkbox" checked={!!sc.isPremium} onChange={(e) => setScene(i, { isPremium: e.target.checked })} /> Premium</label>
                  <label className="flex items-center gap-2 font-sans text-sm"><input type="checkbox" checked={!!sc.isEnding} onChange={(e) => setScene(i, { isEnding: e.target.checked })} /> Fin</label>
                </div>

                <p className="font-ui text-xs tracking-widest text-[#C9A24B] mt-3">CHOIX</p>
                {(sc.choices || []).map((c, ci) => (
                  <div key={ci} className="rounded-lg bg-[#F8F5EE] border border-[#E8DCC4] p-2 mt-2">
                    <div className="grid grid-cols-3 gap-1.5">
                      <input placeholder="Clé (A)" value={c.key || ""} onChange={(e) => setChoice(i, ci, { key: e.target.value })} className={inpS} />
                      <input placeholder="→ destination" value={c.destination || ""} onChange={(e) => setChoice(i, ci, { destination: e.target.value })} className={inpS} />
                      <button onClick={() => removeChoice(i, ci)} className="text-[#A63B36] font-sans text-xs inline-flex items-center gap-1 justify-center"><Trash2 size={13} /> Retirer</button>
                    </div>
                    <input placeholder="Libellé du choix" value={c.label || ""} onChange={(e) => setChoice(i, ci, { label: e.target.value })} className={`${inpS} w-full mt-1.5`} />
                    <div className="grid grid-cols-2 gap-1.5 mt-1.5">
                      <select value={c.requiredObject || ""} onChange={(e) => setChoice(i, ci, { requiredObject: e.target.value })} className={inpS}><option value="">Objet requis : aucun</option>{OBJECTS.map((o) => <option key={o} value={o}>{o}</option>)}</select>
                      <select value={c.objectReward || ""} onChange={(e) => setChoice(i, ci, { objectReward: e.target.value })} className={inpS}><option value="">Objet gagné : aucun</option>{OBJECTS.map((o) => <option key={o} value={o}>{o}</option>)}</select>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 mt-1.5">
                      <label className="flex items-center gap-1 font-sans text-xs"><input type="checkbox" checked={!!c.hidden} onChange={(e) => setChoice(i, ci, { hidden: e.target.checked })} /> Secret (visible seulement avec l'objet)</label>
                    </div>
                    <div className="grid grid-cols-4 gap-1.5 mt-1.5">
                      {C_KEYS.map((k) => (
                        <input key={k} type="number" placeholder={k} value={(c.C_effect || {})[k] || ""} onChange={(e) => setChoice(i, ci, { C_effect: { ...(c.C_effect || {}), [k]: e.target.value } })} className={inpS} />
                      ))}
                    </div>
                  </div>
                ))}
                <button onClick={() => addChoice(i)} className="mt-2 inline-flex items-center gap-1 rounded-full border border-[#C9A24B] text-[#1F2A24] px-3 py-1.5 font-ui text-xs"><Plus size={12} /> Ajouter un choix</button>

                <div className="flex flex-wrap gap-2 mt-3">
                  <button data-testid={`adv-scene-save-${sc.scene_id || "new"}`} onClick={() => saveScene(i)} className="rounded-full bg-[#385E3A] text-white px-5 py-2 font-ui text-xs">ENREGISTRER LA SCÈNE</button>
                  <button data-testid={`adv-preview-${sc.scene_id || "new"}`} onClick={() => setPreview(sc.scene_id)} className="rounded-full border border-[#2E5B7F] text-[#2E5B7F] px-4 py-2 font-ui text-xs inline-flex items-center gap-1"><Eye size={13} /> PRÉVISUALISER</button>
                  <button onClick={() => deleteScene(i)} className="rounded-full border border-[#A63B36] text-[#A63B36] px-4 py-2 font-ui text-xs inline-flex items-center gap-1"><Trash2 size={13} /> Supprimer</button>
                </div>
              </details>
            ))}
          </div>
        </Modal>
      )}

      {preview !== null && (() => {
        const sc = scenes.find((s) => s.scene_id === preview);
        const terminal = sc && (sc.isEnding || !(sc.choices || []).length);
        return (
          <Modal onClose={() => setPreview(null)} testid="adv-preview-modal" title="Prévisualisation">
            <button data-testid="adv-preview-mobile-toggle" onClick={() => setPreviewMobile((m) => !m)} className="mb-3 rounded-full border border-[#2E5B7F] text-[#2E5B7F] px-4 py-1.5 font-ui text-xs">{previewMobile ? "↔ Vue normale" : "📱 Aperçu mobile"}</button>
            {!sc ? (
              <p className="font-sans text-sm text-[#A63B36]">Scène « {preview} » introuvable dans ce livre.</p>
            ) : (
              <div className={previewMobile ? "mx-auto max-w-[380px] rounded-[2rem] border-4 border-[#1F2A24] bg-[#F6F2E9] p-5 shadow-xl" : ""}>
                <p className="font-sans text-xs uppercase tracking-widest text-[#C9A24B]">{sc.character}</p>
                <h3 className="font-serif text-2xl text-[#1F2A24] mt-1">{sc.title} <span className="font-sans text-xs text-[#1F2A24]/40">· étape {sc.step} · {sc.scene_id}{sc.isPremium ? " · premium" : " · gratuit"}</span></h3>
                <p className="font-serif text-[#1F2A24]/85 mt-3 whitespace-pre-line leading-relaxed">{sc.text}</p>
                {sc.journalPrompt && <p className="font-serif italic text-[#1F2A24]/70 mt-3">📓 {sc.journalPrompt}</p>}
                {terminal ? (
                  <p className="mt-5 font-ui text-sm text-[#385E3A]">— FIN DE L'AVENTURE —</p>
                ) : (
                  <div className="mt-5 space-y-2">
                    <p className="font-ui text-xs tracking-widest text-[#1F2A24]/50">QUE CHOISISSEZ-VOUS ?</p>
                    {sc.choices.map((c) => (
                      <button
                        key={c.key}
                        data-testid={`adv-preview-choice-${c.key}`}
                        onClick={() => setPreview(c.destination)}
                        className="w-full text-left rounded-lg border border-[#E8DCC4] hover:border-[#C9A24B] hover:bg-[#C9A24B]/5 p-3 transition-colors"
                      >
                        <span className="font-serif text-[#C9A24B] mr-2">{c.key}</span>
                        <span className="font-sans text-sm text-[#1F2A24]">{c.label}</span>
                        <span className="block font-sans text-xs text-[#1F2A24]/50 mt-1">
                          → {c.destination}
                          {c.requiredObject ? ` · requiert : ${c.requiredObject}` : ""}
                          {c.objectReward ? ` · gagne : ${c.objectReward}` : ""}
                          {c.hidden ? " · ✦ secret" : ""}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
                <p className="font-sans text-xs text-[#1F2A24]/40 mt-4">Aperçu admin : tous les choix sont cliquables (les objets requis sont ignorés ici).</p>
              </div>
            )}
          </Modal>
        );
      })()}

      {showMap && (() => {
        const ids = new Set(scenes.map((s) => s.scene_id));
        const sceneMap = Object.fromEntries(scenes.map((s) => [s.scene_id, s]));
        const broken = [];
        scenes.forEach((s) => (s.choices || []).forEach((c) => {
          if (c.destination && !ids.has(c.destination)) broken.push(`${s.scene_id} · ${c.key} → ${c.destination}`);
        }));
        const bySteps = {};
        scenes.forEach((s) => { (bySteps[s.step] = bySteps[s.step] || []).push(s); });
        const steps = Object.keys(bySteps).map(Number).sort((a, b) => a - b);
        return (
          <Modal onClose={() => setShowMap(false)} testid="adv-map-modal" wide title="Carte du parcours">
            <p className="font-sans text-sm text-[#1F2A24]/70">{scenes.length} scènes · {steps.length} étapes</p>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 font-sans text-[11px]">
              <span className="text-[#385E3A]">● scène gratuite</span>
              <span className="text-[#C9A24B]">● scène premium</span>
              <span className="text-[#2E5B7F]">● scène finale</span>
              <span className="text-[#2E5B7F]">▬ lien normal</span>
              <span className="text-[#385E3A]">▬ vers une fin</span>
              <span className="text-[#A63B36]">▬ lien cassé</span>
              <span className="text-[#1F2A24]/60">✦ choix secret</span>
            </div>
            {broken.length > 0 && (
              <div className="mt-3 rounded-xl border border-[#A63B36]/40 bg-[#A63B36]/5 p-3">
                <p className="font-ui text-xs tracking-wider text-[#A63B36]">⚠ {broken.length} DESTINATION(S) INTROUVABLE(S)</p>
                <ul className="mt-1 font-mono text-xs text-[#A63B36] space-y-0.5">
                  {broken.map((b) => <li key={b}>{b}</li>)}
                </ul>
              </div>
            )}
            <div className="mt-4 space-y-4">
              {steps.map((st) => (
                <div key={st}>
                  <p className="font-ui text-[11px] tracking-widest text-[#C9A24B]">ÉTAPE {st}</p>
                  <div className="mt-1 space-y-2">
                    {bySteps[st].map((s) => (
                      <div key={s.scene_id} className="rounded-lg border border-[#E8DCC4] bg-white p-2.5">
                        <button onClick={() => { setShowMap(false); setPreview(s.scene_id); }} className={`font-serif inline-flex items-center gap-1.5 hover:underline ${(s.isEnding || !(s.choices || []).length) ? "text-[#2E5B7F]" : s.isPremium ? "text-[#C9A24B]" : "text-[#385E3A]"}`}>
                          <Eye size={13} /> {s.scene_id} — {s.title || "(sans titre)"}
                          {!(s.choices || []).length && <span className="font-sans text-[10px] text-[#2E5B7F]">· FIN</span>}
                        </button>
                        {(s.choices || []).length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1.5">
                            {s.choices.map((c) => (
                              <span key={c.key} className={`rounded-full px-2 py-0.5 font-mono text-[11px] ${!ids.has(c.destination) ? "bg-[#A63B36]/15 text-[#A63B36]" : sceneMap[c.destination]?.isEnding || !(sceneMap[c.destination]?.choices || []).length ? "bg-[#385E3A]/15 text-[#385E3A]" : "bg-[#2E5B7F]/10 text-[#2E5B7F]"} ${c.hidden ? "ring-1 ring-[#C9A24B]" : ""}`}>
                                {c.key}→{c.destination}{c.hidden ? "✦" : ""}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Modal>
        );
      })()}
    </div>
  );
}

const inp = "w-full mt-1 rounded-full border border-[#E8DCC4] px-4 py-2 font-sans text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A24B]/40";
const inpS = "rounded-lg border border-[#E8DCC4] px-2.5 py-1.5 font-sans text-xs bg-white focus:outline-none";
const ta = "w-full mt-1 rounded-xl border border-[#E8DCC4] p-3 font-sans text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A24B]/40";

const Field = ({ label, children }) => (
  <div><label className="font-sans text-xs uppercase tracking-wider text-[#1F2A24]/60">{label}</label>{children}</div>
);

const Modal = ({ children, onClose, title, testid, wide }) => (
  <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
    <div data-testid={testid} className={`bg-white rounded-3xl p-6 md:p-8 w-full max-h-[90vh] overflow-y-auto ${wide ? "max-w-3xl" : "max-w-lg"}`} onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-2">
        <h2 className="font-serif text-2xl text-[#1F2A24]">{title}</h2>
        <button onClick={onClose} className="font-sans text-sm text-[#1F2A24]/60 hover:text-[#A63B36]">Fermer</button>
      </div>
      {children}
    </div>
  </div>
);
